import { useCallback, useEffect, useState } from "react";
import { FolderOpen, Loader2, Save, Trash2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError, api } from "@/lib/api";
import { sanitizeModelInputs, type ModelInputs } from "@/lib/financialModelMath";
import { cn } from "@/lib/utils";

/** Mirrors ScenarioResponse in app/routers/financial_models.py. */
interface SavedScenario {
  id: string;
  name: string;
  inputs: Record<string, unknown>;
  listing_id: string | null;
  listing_title: string | null;
  updated_at: string;
}

interface Props {
  /** The inputs on screen now (null while any field is invalid). */
  current: ModelInputs | null;
  /** Loads a saved scenario's inputs into the form. */
  onLoad: (inputs: Partial<ModelInputs>, name: string) => void;
  /** Tie new saves to this listing, and list only its models. */
  listingId?: string;
  /** Suggested name for a new save. */
  defaultName?: string;
}

const errText = (e: unknown, fallback: string) => (e instanceof ApiError && e.message ? e.message : fallback);

/** Named scenarios kept on the buyer's account — they follow them across devices. */
export default function FinancialModelSaved({ current, onLoad, listingId, defaultName }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<SavedScenario[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [name, setName] = useState(defaultName ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const q = listingId ? `?listing_id=${encodeURIComponent(listingId)}` : "";
      setItems(await api.get<SavedScenario[]>(`/financial-models/scenarios${q}`));
      setFailed(false);
    } catch {
      setFailed(true);
      setItems([]);
    }
  }, [listingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (overwrite?: SavedScenario) => {
    if (!current) return;
    const label = overwrite?.name ?? name.trim();
    if (!label) {
      toast.error("Give the model a name first");
      return;
    }
    setBusy(overwrite?.id ?? "new");
    try {
      const row = overwrite
        ? await api.patch<SavedScenario>(`/financial-models/scenarios/${overwrite.id}`, { inputs: current })
        : await api.post<SavedScenario>("/financial-models/scenarios", { name: label, inputs: current, listing_id: listingId ?? null });
      setItems((prev) => [row, ...(prev ?? []).filter((s) => s.id !== row.id)]);
      setActiveId(row.id);
      if (!overwrite) setName("");
      toast.success(overwrite ? `Updated "${row.name}"` : `Saved "${row.name}"`);
    } catch (e) {
      toast.error(errText(e, "Couldn't save the model"));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s: SavedScenario) => {
    if (!confirm(`Delete "${s.name}"?`)) return;
    setBusy(s.id);
    try {
      await api.delete(`/financial-models/scenarios/${s.id}`);
      setItems((prev) => (prev ?? []).filter((x) => x.id !== s.id));
      if (activeId === s.id) setActiveId(null);
    } catch (e) {
      toast.error(errText(e, "Couldn't delete the model"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="rounded-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FolderOpen className="h-4 w-4 text-primary" /> Saved models
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {listingId ? "Models you've saved for this listing." : "Saved to your account — open them on any device."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Name this model, e.g. “Offer at $950K”"
            aria-label="Model name"
          />
          <button
            type="submit"
            disabled={!current || busy !== null}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-gold-gradient px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy === "new" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </form>

        {items === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : failed ? (
          <p className="text-sm text-muted-foreground">Couldn't load your saved models.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing saved yet.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {items.map((s) => (
              <li key={s.id} className={cn("flex items-center gap-2 py-2.5", activeId === s.id && "text-primary")}>
                <button
                  type="button"
                  onClick={() => {
                    onLoad(sanitizeModelInputs(s.inputs), s.name);
                    setActiveId(s.id);
                  }}
                  className="min-w-0 flex-1 text-left"
                  title="Load this model"
                >
                  <div className={cn("truncate text-[0.9375rem] font-semibold", activeId === s.id ? "text-primary" : "text-foreground")}>{s.name}</div>
                  <div className="truncate text-[0.8125rem] text-muted-foreground">
                    {!listingId && s.listing_title ? `${s.listing_title} · ` : ""}
                    {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ""}
                  </div>
                </button>
                {activeId === s.id && (
                  <button
                    type="button"
                    onClick={() => void save(s)}
                    disabled={!current || busy !== null}
                    className="rounded-lg border border-primary/25 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition hover:bg-primary/15 disabled:opacity-50"
                    title="Overwrite with the inputs on screen"
                  >
                    {busy === s.id ? "Saving…" : "Update"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void remove(s)}
                  disabled={busy !== null}
                  className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-rose-500/10 hover:text-rose-500 disabled:opacity-50"
                  aria-label={`Delete ${s.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
